import React, { useEffect, useState } from 'react';
import CommonModal from './CommonModal';
import { callAPI } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';
import { toast } from 'react-toastify';

const ToggleSwitch = ({ checked, onChange }) => (
  <label className="toggleSwitch">
    <input type="checkbox" checked={checked} onChange={onChange} />
    <span className="toggleSlider" />
  </label>
);

/**
 * Deliberately narrow product editor/creator embedded in the POS - the full admin product editor
 * (ingredients, bases, images, delivery-channel config across its own several tabs) stays
 * admin-only; this covers the "most essential" subset: name/short name, category/sub-category,
 * enable/disable, chilli, customisation pop up, and price.
 *
 * Doubles as both "Add Product" and "Edit Product" - `product` is null in create mode. Create
 * mode lets staff pick one or more of the restaurant's real serving sizes (from GET
 * /serving-sizes) with a price per size; if none are picked, it falls back to a single flat
 * price, matching a simple non-multisize product. Edit mode instead shows whatever sizes the
 * product already has configured (via its dish_attributes) as a fixed table - resizing an
 * existing product (adding/removing sizes after creation) stays admin-only.
 */
const ProductEditModal = ({ isOpen, product, categories, subcategories, availableSizes, onClose, onSaved }) => {
  const isCreateMode = !product;

  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [status, setStatus] = useState(1);
  const [cuisineId, setCuisineId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [forceCustomizationPopup, setForceCustomizationPopup] = useState(false);
  const [isChilliEnabled, setIsChilliEnabled] = useState(false);
  const [sizes, setSizes] = useState([]);
  const [price, setPrice] = useState('');
  // Create mode only - lets staff pick one or more of the restaurant's real serving sizes
  // (fetched from GET /serving-sizes) instead of a single flat price. selectedSizeIds preserves
  // the order sizes were checked in so the price rows don't jump around as more are added.
  const [selectedSizeIds, setSelectedSizeIds] = useState([]);
  const [sizePriceDrafts, setSizePriceDrafts] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (product) {
      setName(product.name || '');
      setShortName(product.short_name || '');
      setStatus(product.status);
      setCuisineId(product.cuisine_id != null ? String(product.cuisine_id) : '');
      setCategoryId(product.category_id != null ? String(product.category_id) : '');
      setForceCustomizationPopup(!!product.force_customization_popup);
      setIsChilliEnabled(!!product.is_chilli_enabled);
      setSizes((product.sizes || []).map((s) => ({ ...s })));
    } else {
      setName('');
      setShortName('');
      setStatus(1);
      setCuisineId('');
      setCategoryId('');
      setForceCustomizationPopup(false);
      setIsChilliEnabled(false);
      setSizes([]);
      setPrice('');
      setSelectedSizeIds([]);
      setSizePriceDrafts({});
    }
  }, [product, isOpen]);

  if (!isOpen) return null;

  const updateSizePrice = (index, field, value) => {
    setSizes((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  };

  const toggleSelectedSize = (sizeId) => {
    setSelectedSizeIds((prev) =>
      prev.includes(sizeId) ? prev.filter((id) => id !== sizeId) : [...prev, sizeId]
    );
  };

  const updateSizePriceDraft = (sizeId, field, value) => {
    setSizePriceDrafts((prev) => ({
      ...prev,
      [sizeId]: { ...prev[sizeId], [field]: value },
    }));
  };

  // Sub-category options are scoped to whichever category is currently selected - a product's
  // sub-category always belongs to exactly one category (DishCategory.cid), so showing every
  // restaurant's sub-category regardless of the chosen category would let staff pick a mismatched
  // pair.
  const filteredSubcategories = (subcategories || []).filter(
    (sc) => String(sc.cuisine_id) === String(cuisineId)
  );

  const handleSave = async () => {
    if (!name.trim()) {
      toast.warning('Product name cannot be empty');
      return;
    }
    if (!cuisineId) {
      toast.warning('Please select a category');
      return;
    }

    setSaving(true);
    try {
      if (isCreateMode) {
        const sizesPayload = selectedSizeIds.map((sizeId) => {
          const dineIn = parseFloat(sizePriceDrafts[sizeId]?.dine_in_price) || 0;
          const websiteDraft = sizePriceDrafts[sizeId]?.website_price;
          return {
            size_id: sizeId,
            // Left blank, the website price takes the dine-in figure — a base price of 0 would
            // otherwise remove the fallback the ordering screens rely on.
            website_price: websiteDraft === '' || websiteDraft === undefined
              ? dineIn
              : parseFloat(websiteDraft) || 0,
            dine_in_price: dineIn,
            takeaway_price: parseFloat(sizePriceDrafts[sizeId]?.takeaway_price) || 0,
          };
        });
        const hasSizes = sizesPayload.length > 0;

        const res = await callAPI(apiUrls.PRODUCT_MANAGEMENT_CREATE_PRODUCT, {}, 'POST', {
          name: name.trim(),
          short_name: shortName.trim(),
          cuisine_id: cuisineId,
          category_id: categoryId || null,
          status,
          price: hasSizes ? null : (price === '' ? null : parseFloat(price) || 0),
          sizes: hasSizes ? sizesPayload : undefined,
          force_customization_popup: forceCustomizationPopup,
          is_chilli_enabled: isChilliEnabled,
        }, {});

        toast.success('Product created');
        const sizeTitleById = {};
        (availableSizes || []).forEach((s) => { sizeTitleById[s.id] = s.title; });

        const cuisineName = (categories || []).find((c) => String(c.id) === String(cuisineId))?.name || null;
        const categoryName = (subcategories || []).find((c) => String(c.id) === String(categoryId))?.name || null;

        onSaved({
          id: res.data?.data?.id,
          name: name.trim(),
          short_name: shortName.trim(),
          status,
          cuisine_id: cuisineId,
          cuisine_name: cuisineName,
          category_id: categoryId || null,
          category_name: categoryId ? categoryName : null,
          force_customization_popup: forceCustomizationPopup,
          is_chilli_enabled: isChilliEnabled,
          is_multisize_enabled: hasSizes,
          sizes: hasSizes
            ? sizesPayload.map((s) => ({ ...s, size_name: sizeTitleById[s.size_id] || '' }))
            : (price !== '' ? [{
              size_id: null,
              size_name: 'Standard',
              // Mirrors what the backend does with a flat price: it seeds all three channels.
              website_price: parseFloat(price) || 0,
              dine_in_price: parseFloat(price) || 0,
              takeaway_price: parseFloat(price) || 0,
            }] : []),
        }, true);
      } else {
        const prices = sizes.map((s) => ({
          size_id: s.size_id,
          website_price: parseFloat(s.website_price) || 0,
          dine_in_price: parseFloat(s.dine_in_price) || 0,
          takeaway_price: parseFloat(s.takeaway_price) || 0,
        }));

        await callAPI(apiUrls.PRODUCT_MANAGEMENT_UPDATE_PRODUCT, {}, 'POST', {
          id: product.id,
          name: name.trim(),
          short_name: shortName.trim(),
          status,
          cuisine_id: cuisineId,
          category_id: categoryId,
          prices,
          force_customization_popup: forceCustomizationPopup,
          is_chilli_enabled: isChilliEnabled,
        }, {});

        toast.success('Product updated');
        const cuisineName = (categories || []).find((c) => String(c.id) === String(cuisineId))?.name || null;
        const categoryName = (subcategories || []).find((c) => String(c.id) === String(categoryId))?.name || null;
        onSaved({
          ...product,
          name: name.trim(),
          short_name: shortName.trim(),
          status,
          cuisine_id: cuisineId,
          cuisine_name: cuisineName,
          category_id: categoryId || null,
          category_name: categoryId ? categoryName : null,
          force_customization_popup: forceCustomizationPopup,
          is_chilli_enabled: isChilliEnabled,
          sizes,
        }, false);
      }
    } catch (err) {
      console.error('Failed to save product', err);
      toast.error('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  return (
    <CommonModal isOpen={isOpen} onClose={onClose}>
      <div className="modal-header">
        <h4>{isCreateMode ? 'Add Product' : 'Edit Product'}</h4>
        <button className="btn-close" onClick={onClose}></button>
      </div>

      <div className="modal-body">
        <div className="row g-3 mb-3">
          <div className="col-md-8">
            <div className="formStyle">
              <label>Product Name</label>
              <input
                type="text"
                className="form-control"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="formStyle">
              <label>Short Name</label>
              <input
                type="text"
                className="form-control"
                value={shortName}
                onChange={(e) => setShortName(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <div className="formStyle">
              <label>Category</label>
              <select
                className="form-control"
                value={cuisineId}
                onChange={(e) => {
                  setCuisineId(e.target.value);
                  setCategoryId('');
                }}
              >
                <option value="">Select category</option>
                {(categories || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-6">
            <div className="formStyle">
              <label>Sub Category</label>
              <select
                className="form-control"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                disabled={!cuisineId}
              >
                <option value="">None</option>
                {filteredSubcategories.map((sc) => (
                  <option key={sc.id} value={sc.id}>{sc.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="d-flex flex-wrap gap-4 mb-3">
          <div className="d-flex align-items-center gap-2">
            <ToggleSwitch checked={status === 1} onChange={(e) => setStatus(e.target.checked ? 1 : 0)} />
            <span>{status === 1 ? 'Enabled' : 'Disabled'}</span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <ToggleSwitch checked={isChilliEnabled} onChange={(e) => setIsChilliEnabled(e.target.checked)} />
            <span>Spice Level</span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <ToggleSwitch checked={forceCustomizationPopup} onChange={(e) => setForceCustomizationPopup(e.target.checked)} />
            <span>Customisation Pop Up</span>
          </div>
        </div>

        {isCreateMode ? (
          <>
            {(availableSizes || []).length > 0 && (
              <div className="mb-3">
                <label className="d-block mb-2">Sizes (select one or more)</label>
                {availableSizes.map((size) => {
                  const checked = selectedSizeIds.includes(size.id);
                  return (
                    <div key={size.id} className="mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSelectedSize(size.id)}
                        />
                        <span>{size.title}</span>
                      </div>
                      {checked && (
                        <div className="row g-2 mt-1" style={{ maxWidth: 600 }}>
                          <div className="col-4">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              className="form-control"
                              placeholder="Website Price"
                              value={sizePriceDrafts[size.id]?.website_price ?? ''}
                              onChange={(e) => updateSizePriceDraft(size.id, 'website_price', e.target.value)}
                            />
                          </div>
                          <div className="col-4">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              className="form-control"
                              placeholder="Dine-in Price"
                              value={sizePriceDrafts[size.id]?.dine_in_price ?? ''}
                              onChange={(e) => updateSizePriceDraft(size.id, 'dine_in_price', e.target.value)}
                            />
                          </div>
                          <div className="col-4">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              className="form-control"
                              placeholder="Takeaway Price"
                              value={sizePriceDrafts[size.id]?.takeaway_price ?? ''}
                              onChange={(e) => updateSizePriceDraft(size.id, 'takeaway_price', e.target.value)}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {selectedSizeIds.length === 0 && (
              <div className="formStyle mb-3" style={{ maxWidth: 220 }}>
                <label>Price</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-control"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                />
              </div>
            )}
          </>
        ) : (
          <>
            <label className="d-block mb-2">Prices</label>
            {/* Three channels per size, in the same order and with the same names the admin's
                Configure Size tab uses (Website / Dine in / Takeaway POS), so a manager moving
                between the two screens is looking at the same three columns. */}
            <table className="table">
              <thead>
                <tr>
                  <th>Size</th>
                  <th>Website Price</th>
                  <th>Dine-in Price</th>
                  <th>Takeaway Price</th>
                </tr>
              </thead>
              <tbody>
                {sizes.map((size, i) => (
                  <tr key={size.size_id ?? 'standard'}>
                    <td>{size.size_name}</td>
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-control"
                        value={size.website_price ?? 0}
                        onChange={(e) => updateSizePrice(i, 'website_price', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-control"
                        value={size.dine_in_price}
                        onChange={(e) => updateSizePrice(i, 'dine_in_price', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-control"
                        value={size.takeaway_price}
                        onChange={(e) => updateSizePrice(i, 'takeaway_price', e.target.value)}
                      />
                    </td>
                  </tr>
                ))}
                {sizes.length === 0 && (
                  <tr><td colSpan={4}>No prices configured yet - use the full admin editor to set up sizes.</td></tr>
                )}
              </tbody>
            </table>
            <p className="text-muted" style={{ fontSize: 12 }}>
              Website is the base price. Dine-in and takeaway fall back to it when left at 0, which
              is why a product can show a price on the ordering screen with 0 in its own column.
            </p>
          </>
        )}
      </div>

      <div className="modal-footer justify-content-between">
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </CommonModal>
  );
};

export default ProductEditModal;
